import { Button, Space, Tooltip, Dropdown, ConfigProvider } from "antd";
import type { MenuProps } from "antd";
import { BaseButtonProps } from "antd/es/button/button";
import { MoreOutlined, LinkOutlined } from "@ant-design/icons";
import { Link as RRDLink } from "react-router-dom";

import { isMobile } from 'react-device-detect';
import React from "react";


/** Default icon for the dropdown button */
const defaultDropdownIcon = <MoreOutlined />;

/** Default icon for an item without an icon */
const defaultItemIcon = <LinkOutlined />;

/** Default label if one isn't specified for the dropdown */
const defaultActionsLabel = 'Ações';


//#region Actions interfaces


/**
 * A single button/item in the dropdown
 * 
 * @property {string} label
 * @property {React.ReactNode?} icon
 * @property {React.MouseEventHandler?} onClick
 * @property {string?} linkTo
 * @property {(boolean|((item:ActionsItem)=>boolean))?} enabled
 */
interface ActionsItem {
	/** The text to show for the dropdown button or tooltip */
	label: string;
	
	/**
	 * The icon to show on the button
	 * @default LinkOutlined
	 */
	icon?: React.ReactNode;
	
	/**
	 * A function to run when the button is clicked\
	 * Ignored if `linkTo` is set
	 * @this EventTarget
	 */
	onClick?: React.MouseEventHandler;
	
	/** If set, navigates to this URL */
	linkTo?: string;
	
	/**
	 * Indicates if the item is enabled or not\
	 * Any disabled item is skipped\
	 * In case of being a function, will run before rendering\
	 * The function ***MAY*** execute multiple times
	 */
	enabled?: boolean | ((item: ActionsItem) => boolean);
}



/**
 * A sub-menu/dropdown to show instead of a button\
 * For mobile, will be displayed as regular items inside the dropdown\
 * For desktop, will be displayed as a dropdown
 * 
 * @property {string} label
 * @property {ReactNode?} icon
 * @property {ActionsItem[]} items
 * @property {(boolean|((item:ActionsSubMenu)=>boolean))?} enabled
 */
interface ActionsSubMenu {
	/** The text to show for the tooltip */
	label: string;
	
	/**
	 * The icon to show on the button
	 * @default MoreOutlined
	 */
	icon?: React.ReactNode;
	
	/** The items that are part of the menu */
	items: ActionsItem[];
	
	/**
	 * Indicates if the sub-menu is enabled or not\
	 * If it is disabled, it won't be rendered\
	 * In case of being a function, will run before rendering\
	 * If it doesn't have any enabled children, it will stay disabled
	 */
	enabled?: boolean | ((item: ActionsSubMenu) => boolean);
}



/**
 * Parameters to pass to the `<GenericTable.Actions>` element\
 * These are related to antd's `<Button>`, and have to be separated
 */
interface ActionsButtonParameters {
	/**
	 * The type of button to use
	 * @default 'default'
	 */
	type?: BaseButtonProps['type'];
	
	/**
	 * The icon to show on the dropdown
	 * @default defaultDropdownIcon
	 */
	dropdownIcon?: BaseButtonProps['icon'];
	
	/**
	 * The default icon to show for an action without a link
	 * @default defaultItemIcon
	 */
	defaultIcon?: BaseButtonProps['icon'];
}



/**
 * Parameters to pass to the `<GenericTable.Actions>` element
 * @extends ActionsButtonParameters
 */
interface ActionsParameters extends ActionsButtonParameters {
	/**
	 * The text to show for the dropdown button or tooltip
	 * @default 'Ações'
	 */
	label?: string;
	
	/** The items to show in the dropdown or as buttons */
	items: (ActionsItem | ActionsSubMenu)[];
	
	/**
	 * Indicates if the actions element is enabled or not\
	 * If it is disabled, it won't be rendered\
	 * In case of being a function, will run before rendering
	 */
	enabled?: boolean | (() => boolean);
	
	/**
	 * Indicates if the placeholder element renders when the actions are disabled
	 * @default true
	 */
	hideIfDisabled?: boolean;
	
	/**
	 * Indicates if the placeholder element renders when there are no items\
	 * This can happen in the following situations:
	 * - No `items` were provided
	 * - There were no active `items`
	 * @default false
	 */
	hideIfEmpty?: boolean;
	
	/**
	 * Indicates if the dropdown should be used if there's a single item\
	 * If set to `true`, will render a dropdown at all times\
	 * If set to `false`, will render the item as a pill button
	 * @default false
	 */
	mobileDropdownSingleItem?: boolean;
}


//#endregion



//#region Utility functions


/** Creates a dropdown menu with the items set */
const ActionsDropdown = function ActionsDropdown({
	label,
	icon,
	items,
	type = 'default',
	dropdownIcon = defaultDropdownIcon,
	defaultIcon = defaultItemIcon
}: ActionsSubMenu & ActionsButtonParameters): JSX.Element {
	let menuProps: MenuProps = {
		items: items.map((item, index) => ({
			key: index.toString(),
			label: 'linkTo' in item && item.linkTo
				? (<>
					<RRDLink to={item.linkTo}>
						{item.label}
					</RRDLink>
				</>)
				: item.label,
			icon: item.icon || defaultIcon,
		})),
		onClick: ({ key, domEvent }) => {
			let index = Number.parseInt(key);
			
			if(Number.isNaN(index) || !items[index]?.onClick) {
				return;
			}
			
			items[index]?.onClick?.call(
				domEvent.target,
				/*
					TypeScript gets super weird about this...
					The `domEvent` can be a keyboard event, but we're handling clicking here.
					As such, we "lie" to make everybody happy
				*/
				domEvent as React.MouseEvent<HTMLElement, MouseEvent>
			);
		}
	};
	
	let menu = (<>
		<Dropdown menu={menuProps} placement="bottomRight" arrow>
			<Button type={type} shape="circle" size="middle" icon={icon || dropdownIcon} title={label} />
		</Dropdown>
	</>);
	
	return isMobile
		? (<>
			<ConfigProvider theme={{
				components: {
					Menu: {
						iconSize: 16,
						itemHeight: 40,
					}
				},
				token: {
					fontSize: 16,
					fontSizeLG: 16,
				}
			}}>
				{menu}
			</ConfigProvider>
		</>)
		: menu;
};



/** Placeholder element to be rendered when there are no actions */
const ActionsPlaceholder = function ActionsPlaceholder({
	type = "default",
	dropdownIcon = defaultDropdownIcon
}: ActionsButtonParameters): JSX.Element {
	return (<>
		<Button type={type} shape="circle" size="middle" icon={dropdownIcon} disabled={true} />
	</>);
};



/** Parses the value of {@see ActionsParameters.enabled} to boolean */
const isActionsEnabled = function isActionsEnabled(enabled?: boolean | (() => boolean)): boolean {
	if(enabled === undefined) {
		return true;
	} else if(typeof enabled === 'function') {
		return enabled();
	}
	
	return enabled;
};



/** Checks if an item is enabled or not */
const isItemEnabled = function isItemEnabled(item: ActionsItem): boolean {
	if(item.enabled === undefined) {
		return true;
	} else if(typeof item.enabled === 'function') {
		return item.enabled(item);
	}
	
	return item.enabled;
};



/** Checks if a sub menu is enabled or not */
const isSubMenuEnabled = function isSubMenuEnabled(item: ActionsSubMenu): boolean {
	if(item.enabled === false) {
		return false;
	}
	
	let enabled: boolean = false;
	
	for(let child of item.items) {
		if(isItemEnabled(child)) {
			enabled = true;
			break;
		}
	}
	
	return enabled && (typeof item.enabled === 'function')
		? item.enabled(item)
		: enabled;
};


//#endregion



//#region Actions element


/** Element to render the table actions' column */
export default function Actions({
	label,
	items,
	enabled,
	type = 'default',
	dropdownIcon = defaultDropdownIcon,
	defaultIcon = defaultItemIcon,
	hideIfDisabled = true,
	hideIfEmpty = false,
	mobileDropdownSingleItem = false,
}: ActionsParameters): JSX.Element {
	if(!items.length || !isActionsEnabled(enabled)) {
		return hideIfDisabled
			? (<></>)
			: (<>
				<ActionsPlaceholder type={type} dropdownIcon={dropdownIcon} />
			</>);
	}
	
	if(isMobile) {
		let flatItems: ActionsItem[] = [];
		
		for(let item of items) {
			if('items' in item) {
				flatItems = flatItems.concat(item.items);
			} else {
				flatItems.push(item);
			}
		}
		
		flatItems = flatItems.filter(isItemEnabled);
		
		if(!flatItems.length) {
			return hideIfEmpty
				? (<></>)
				: (<>
					<ActionsPlaceholder type={type} dropdownIcon={dropdownIcon} />
				</>);
		} else if (
			mobileDropdownSingleItem
			|| (
				!mobileDropdownSingleItem
				&& flatItems.length > 1
			)
		) {
			return (<>
				<Space size="large" direction="horizontal">
					<ActionsDropdown type={type} label={label || defaultActionsLabel} items={flatItems} dropdownIcon={dropdownIcon} defaultIcon={defaultIcon} />
				</Space>
			</>);
		}
		
		let item = flatItems[0];
		
		let button = (<>
			<Button type={type} shape="round" size="middle" icon={item.icon || defaultIcon} onClick={item.linkTo ? undefined : item.onClick}>
				{item.label}
			</Button>
		</>);
		
		if(item.linkTo) {
			return (<>
				<RRDLink to={item.linkTo}>
					{button}
				</RRDLink>
			</>);
		}
		
		return button;
	} else {
		let children = [];
		
		for(let item of items) {
			if('items' in item) {
				if(!isSubMenuEnabled(item)) {
					continue;
				}
				
				children.push(<>
					<ActionsDropdown type={type} label={item.label || defaultActionsLabel} items={item.items} icon={item.icon || dropdownIcon} />
				</>);
			} else {
				if(!isItemEnabled(item)) {
					continue;
				}
				
				children.push(<>
					<Tooltip placement="top" title={item.label}>
						<Button type={type} shape="circle" size="middle" icon={item.icon || defaultIcon} onClick={item.onClick} />
					</Tooltip>
				</>);
			}
		}
		
		return (<>
			<Space size="small" direction="horizontal">
				{ children.length ? children : !hideIfEmpty && <ActionsPlaceholder type={type} dropdownIcon={dropdownIcon} />}
			</Space>
		</>);
	}
};

//#endregion
